import React, { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  XMarkIcon,
  PrinterIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

/**
 * PrescriptionModal - Modal para generar receta/indicaciones al alta
 * Reutiliza el mismo encabezado y pie de página profesional del certificado
 */
const PrescriptionModal = ({ 
  isOpen, 
  onClose, 
  paciente, 
  ficha, 
  currentUser,
  formatDate 
}) => {
  const [prescriptionData, setPrescriptionData] = useState({
    medicamentos: ficha?.indicaciones || '',
    indicaciones_cuidado: '',
    proxima_cita: ''
  });

  const handlePrint = () => {
    window.print();
  };

  if (!isOpen || !paciente || !ficha) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 overflow-hidden h-full w-full z-50"
      onClick={onClose}
    >
      <div 
        className="absolute inset-4 sm:inset-8 mx-auto max-w-6xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white print:hidden">
          <div className="flex items-center gap-3">
            <ClipboardDocumentListIcon className="w-6 h-6 text-[#2A9D8F]" strokeWidth={1.5} />
            <h3 className="text-xl font-bold text-gray-900">Generar Indicaciones</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>

        {/* Split View Container */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden print:block">
          {/* Panel Izquierdo - Controles (40%) */}
          <div className="w-full md:w-2/5 p-6 overflow-y-auto bg-white border-r border-gray-100 print:hidden">
            <h4 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-4">
              Configuración de Indicaciones
            </h4>
            
            <div className="space-y-5">
              {/* Medicamentos / Productos */}
              <div>
                <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                  Rx / Medicamentos / Productos
                </label>
                <textarea
                  value={prescriptionData.medicamentos}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, medicamentos: e.target.value })}
                  rows="5"
                  placeholder="Ej: Aplicar crema X 2 veces al día, Usar plantillas ortopédicas..."
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                    focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-y"
                />
              </div>

              {/* Indicaciones de Cuidado */}
              <div>
                <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                  Indicaciones de Cuidado
                </label>
                <textarea
                  value={prescriptionData.indicaciones_cuidado}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, indicaciones_cuidado: e.target.value })}
                  rows="5"
                  placeholder="Ej: No mojar por 24hrs, evitar calzado cerrado, reposo relativo..."
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                    focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base resize-y"
                />
              </div>

              {/* Próxima Cita */}
              <div>
                <label className="block text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-2">
                  Próxima Cita (Opcional)
                </label>
                <input
                  type="date"
                  value={prescriptionData.proxima_cita}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, proxima_cita: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                    focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] text-base"
                />
              </div>
            </div>
          </div>

          {/* Panel Derecho - Previsualización (60%) */}
          <div className="w-full md:w-3/5 bg-gray-100 p-6 overflow-y-auto print:bg-white print:p-0">
            <div className="md:hidden text-xs font-bold text-[#2A9D8F] uppercase tracking-wide mb-4 print:hidden">
              Previsualización
            </div>

            {/* Alerta de Perfil Incompleto */}
            {(!currentUser?.rut_profesional || !currentUser?.rol_minsal || !currentUser?.direccion_consulta) && (
              <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-md flex items-start gap-3 print:hidden">
                <svg className="h-6 w-6 text-yellow-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className="text-sm font-bold text-yellow-800">
                    Faltan datos en tu perfil profesional
                  </h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    El documento se generará sin tu RUT, ROL o Dirección. 
                    <a 
                      href="/app/equipo"
                      className="underline font-semibold ml-1 hover:text-yellow-900"
                    >
                      Configurar ahora
                    </a>.
                  </p>
                </div>
              </div>
            )}
            
            {/* Hoja de papel */}
            <div className="bg-white shadow-xl rounded-lg p-8 max-w-[21cm] mx-auto print:shadow-none print:p-0 print:max-w-none indicaciones-contenedor">
              {/* Título */}
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold mb-2">INDICACIONES PODOLÓGICAS</h1>
                <p className="text-gray-600">Receta / Indicaciones al Alta</p>
              </div>

              {/* Datos del Paciente */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Paciente:</span>
                    <p className="font-semibold text-gray-900">{paciente.nombre}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">RUT:</span>
                    <p className="font-semibold text-gray-900">{paciente.rut}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Fecha de atención:</span>
                    <p className="font-semibold text-gray-900">{formatDate(ficha.fecha)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Fecha de emisión:</span>
                    <p className="font-semibold text-gray-900">{format(new Date(), "d 'de' MMMM 'de' yyyy", { locale: es })}</p>
                  </div>
                </div>
              </div>

              {/* Sección Rx / Indicaciones */}
              <div className="mb-6 space-y-4">
                {prescriptionData.medicamentos && (
                  <div className="border-l-4 border-[#2A9D8F] pl-4">
                    <h2 className="font-bold text-gray-900 mb-2">Rx / Medicamentos / Productos</h2>
                    <p className="text-gray-700 whitespace-pre-wrap">{prescriptionData.medicamentos}</p>
                  </div>
                )}

                {prescriptionData.indicaciones_cuidado && (
                  <div className="border-l-4 border-[#E76F51] pl-4">
                    <h2 className="font-bold text-gray-900 mb-2">Indicaciones de Cuidado</h2>
                    <p className="text-gray-700 whitespace-pre-wrap">{prescriptionData.indicaciones_cuidado}</p>
                  </div>
                )}

                {prescriptionData.proxima_cita && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800">
                      <strong>Próxima Cita Sugerida:</strong> {format(new Date(prescriptionData.proxima_cita + 'T12:00:00'), "EEEE d 'de' MMMM 'de' yyyy", { locale: es })}
                    </p>
                  </div>
                )}
              </div>

              {/* Nota Legal */}
              <div className="mb-8 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 text-center">
                  ⚠️ Este documento contiene indicaciones podológicas y no reemplaza una receta médica retenida.
                </p>
              </div>

              {/* Fecha y Lugar */}
              <div className="text-center mb-8">
                <p>{currentUser?.cuenta?.ciudad || currentUser?.ciudad_consulta || ''}, {format(new Date(), "d 'de' MMMM 'de' yyyy", { locale: es })}</p>
              </div>

              {/* Pie de Página - Datos Profesionales (igual que certificado) */}
              <div className="mt-16 pt-8 border-t border-gray-300">
                <div className="text-center">
                  <div className="mb-4 h-20">
                    {/* Espacio para firma */}
                  </div>
                  <p className="font-medium">{currentUser?.first_name} {currentUser?.last_name}</p>
                  <p>{currentUser?.titulo_profesional || 'Podólogo/a Clínico/a'}</p>
                  <p>RUT: {currentUser?.rut_profesional || 'No registrado'}</p>
                  <p>ROL Minsal: N° {currentUser?.rol_minsal || 'Pendiente'}</p>
                  <p className="text-sm text-gray-500 mt-2">{currentUser?.direccion_consulta || 'Dirección no registrada'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Fijo */}
        <div className="px-6 py-4 bg-white border-t border-gray-100 flex justify-end gap-3 print:hidden">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handlePrint}
            className="px-6 py-3 bg-[#2A9D8F] hover:bg-[#21867a] text-white rounded-lg shadow-lg transition-all font-medium flex items-center gap-2"
          >
            <PrinterIcon className="w-5 h-5" strokeWidth={1.5} />
            Imprimir Indicaciones
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionModal;
